<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">财富合同</h1>
            <p class="list-page-description">查看合同本金、资金批次、流水和合同附件。</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
      <PageFilterBar class="mx-6" body-class="p-5" :show-refresh="true" :refresh-loading="loading" @refresh="loadContracts">
        <div class="w-44">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">合同编号</label>
          <input v-model="filters.contractNo" class="form-input form-control-sm w-full" placeholder="合同编号">
        </div>
        <div class="w-52">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">合同名称</label>
          <input v-model="filters.contractName" class="form-input form-control-sm w-full" placeholder="合同名称">
        </div>
        <div class="w-36">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">客户ID</label>
          <input v-model.number="filters.wealthCustomerId" type="number" class="form-input form-control-sm w-full">
        </div>
        <div class="w-36">
          <label class="block text-xs font-medium text-slate-500 mb-1.5">状态</label>
          <select v-model="filters.status" class="form-select-native form-control-sm w-full">
            <option value="">全部</option>
            <option v-for="item in contractStatusOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
    </template>

    <div class="list-page-table-panel mx-6">
      <div class="data-table-shell wealth-contract-table-layout">
        <div class="data-table-scroll wealth-contract-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">合同</th>
                <th class="data-table-th text-left">客户ID</th>
                <th class="data-table-th text-right">当前本金</th>
                <th class="data-table-th text-left">签署日期</th>
                <th class="data-table-th text-left">状态</th>
                <th class="data-table-th text-left">创建时间</th>
                <th class="data-table-th text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="7" class="data-table-empty">加载中...</td></tr>
              <tr v-else-if="rows.length === 0"><td colspan="7" class="data-table-empty">暂无合同</td></tr>
              <tr v-for="row in rows" v-else :key="row.id" class="data-table-row">
                <td class="data-table-td"><div class="font-medium text-slate-900">{{ row.contractName }}</div><div class="font-mono text-xs text-slate-500">{{ row.contractNo }}</div></td>
                <td class="data-table-td">{{ row.wealthCustomerId }}</td>
                <td class="data-table-td is-right font-medium text-slate-900">¥{{ formatMoney(row.currentPrincipalAmount) }}</td>
                <td class="data-table-td">{{ formatDate(row.signDate) }}</td>
                <td class="data-table-td"><WealthStatusBadge type="contract" :value="row.status" show-dot /></td>
                <td class="data-table-td text-xs text-slate-600">{{ formatDateTime(row.createTime) }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button class="table-action-button is-primary" title="详情" @click="openDetail(row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button v-if="row.status === 'ACTIVE'" v-permission="'wealth:fund:increase'" class="table-action-button is-success" title="增资" @click="openApply('INCREASE', row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                    </button>
                    <button v-if="row.status === 'ACTIVE'" v-permission="'wealth:fund:decrease'" class="table-action-button is-warning" title="减资" @click="openApply('DECREASE', row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/></svg>
                    </button>
                    <button v-if="row.status === 'ACTIVE'" v-permission="'wealth:fund:withdraw'" class="table-action-button is-danger" title="撤资" @click="openApply('WITHDRAW', row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <AppPagination v-if="pagination.total > 0" class="pagination-bridge wealth-contract-pagination-sticky" :pagination="pagination" :page-sizes="PAGE_SIZE_OPTIONS" mode="simple" @page-change="handlePageChange" @size-change="handleSizeChange" />
    </div>

    <WealthOperationApplyModal v-model="applyVisible" :operation-type="applyType" :preset="applyPreset" @submitted="loadContracts" />

    <WealthDetailDrawer v-model="detailVisible" title="财富合同详情" :subtitle="selectedContract?.contractNo">
      <div v-if="selectedContract" class="space-y-5">
        <section class="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-4">
          <div><div class="text-xs text-slate-500">合同名称</div><div class="font-medium text-slate-900">{{ selectedContract.contractName }}</div></div>
          <div><div class="text-xs text-slate-500">客户ID</div><div>{{ selectedContract.wealthCustomerId }}</div></div>
          <div><div class="text-xs text-slate-500">当前本金</div><div class="font-medium text-slate-900">¥{{ formatMoney(selectedContract.currentPrincipalAmount) }}</div></div>
          <div><div class="text-xs text-slate-500">状态</div><WealthStatusBadge type="contract" :value="selectedContract.status" /></div>
        </section>
        <WealthBatchPanel :contract-id="selectedContract.id" title="合同批次" :show-mode-switch="false">
          <template #actions="{ row }">
            <div class="flex justify-center gap-1">
              <button v-if="selectedContract?.status === 'ACTIVE'" v-permission="'wealth:fund:decrease'" class="table-action-button is-warning" title="减资" @click="openApply('DECREASE', selectedContract)">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/></svg>
              </button>
              <button v-if="selectedContract?.status === 'ACTIVE'" v-permission="'wealth:fund:rollover'" class="table-action-button is-indigo" title="续期" @click="openRollover(row)">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 3h5v5"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 8a9 9 0 00-15.5-3.5"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 21H3v-5"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 16a9 9 0 0015.5 3.5"/>
                </svg>
              </button>
            </div>
          </template>
        </WealthBatchPanel>
        <section class="rounded-lg border border-slate-200">
          <div class="border-b border-slate-200 px-4 py-3">
            <h4 class="text-sm font-semibold text-slate-900">合同流水</h4>
          </div>
          <div class="data-table-shell border-0 shadow-none">
            <div class="data-table-scroll">
              <table class="data-table text-sm">
                <thead class="data-table-head"><tr><th class="data-table-th text-left">流水</th><th class="data-table-th text-left">类型</th><th class="data-table-th text-right">金额</th><th class="data-table-th text-left">时间</th></tr></thead>
                <tbody>
                  <tr v-if="ledgers.length === 0"><td colspan="4" class="data-table-empty">暂无流水</td></tr>
                  <tr v-for="item in ledgers" v-else :key="item.id" class="data-table-row">
                    <td class="data-table-td font-mono text-xs">{{ item.ledgerNo }}</td>
                    <td class="data-table-td"><WealthStatusBadge type="operation" :value="item.operationType" /></td>
                    <td class="data-table-td is-right">¥{{ formatMoney(item.amount) }}</td>
                    <td class="data-table-td text-xs">{{ formatDateTime(item.createTime) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <WealthAttachmentPanel
          title="合同附件"
          biz-type="CONTRACT"
          :biz-id="selectedContract.id"
          :can-manage="true"
        />
      </div>
    </WealthDetailDrawer>
  </ListPageShell>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getWealthContractLedgerPage, getWealthContractPage } from '@/api/wealth'
import WealthAttachmentPanel from '../components/WealthAttachmentPanel.vue'
import WealthBatchPanel from '../components/WealthBatchPanel.vue'
import WealthDetailDrawer from '../components/WealthDetailDrawer.vue'
import WealthOperationApplyModal from '../components/WealthOperationApplyModal.vue'
import WealthStatusBadge from '../components/WealthStatusBadge.vue'
import { cleanPayload, contractStatusOptions, formatDate, formatDateTime, formatMoney, PAGE_SIZE_OPTIONS, pagePayload, syncPageResult } from '../utils'

const filters = reactive({ contractNo: '', contractName: '', wealthCustomerId: null, status: '' })
const pagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const rows = ref([])
const loading = ref(false)
const detailVisible = ref(false)
const selectedContract = ref(null)
const ledgers = ref([])
const applyVisible = ref(false)
const applyType = ref('INCREASE')
const applyPreset = ref({})

async function loadContracts() {
  loading.value = true
  try {
    const result = await getWealthContractPage(pagePayload(pagination, cleanPayload(filters)))
    rows.value = result?.records || []
    syncPageResult(pagination, result)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pagination.current = 1
  pagination.page = 1
  loadContracts()
}

function handleReset() {
  Object.assign(filters, { contractNo: '', contractName: '', wealthCustomerId: null, status: '' })
  handleSearch()
}

function handlePageChange(page) {
  pagination.current = page
  pagination.page = page
  loadContracts()
}

function handleSizeChange(size) {
  pagination.size = size
  handleSearch()
}

async function openDetail(row) {
  selectedContract.value = row
  detailVisible.value = true
  const result = await getWealthContractLedgerPage(row.id, { page: 1, size: 10 })
  ledgers.value = result?.records || []
}

function openApply(type, contract) {
  applyType.value = type
  applyPreset.value = { contractId: contract.id, wealthCustomerId: contract.wealthCustomerId }
  applyVisible.value = true
}

function openRollover(batch) {
  applyType.value = 'ROLLOVER'
  applyPreset.value = { contractId: selectedContract.value?.id, batchId: batch.id }
  applyVisible.value = true
}

onMounted(loadContracts)
</script>
